/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import React, { useState, useEffect } from "react";

import { Typography, Button, Tooltip, TextField, lighten, darken, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { Effect, Setter, Setting } from "../../../../utils/Interfaces";

export interface ListSettingProps {
    reloadSettings: Effect;
    setting: Setting;
    updateSetting: Setter<string>;
}

const ListSetting = (props: ListSettingProps) => {
    const { reloadSettings, setting, updateSetting } = props;

    const [editing, setEditing] = useState<boolean>(false);
    const [listItems, setListItems] = useState<string[]>([]);
    const [listItemAdd, setListItemAdd] = useState<string>("");

    const theme = useTheme();

    function removeEntryFromList(entry: string) {
        let tempItems = listItems.filter((listEntry) => listEntry !== entry);
        setListItems(tempItems);
    }

    function addEntryToList(entry: string) {
        let tempItems = [];
        // Create new array to make it functionally immutable.
        listItems.map((listEntry) => tempItems.push(listEntry));
        tempItems.push(entry);
        setListItems(tempItems);
    }

    function resetListEntries() {
        let tempItems: string[] = [];
        let settingAsList: string[] = setting.value as string[];
        settingAsList.map((entry: string) => tempItems.push(entry));
        setListItems(tempItems);
    }

    useEffect(() => {
        resetListEntries();
    }, [setting]);

    if (!editing) {
        return (
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                <Box sx={{ flexGrow: 1 }}>
                    {listItems.map((entry, index) => {
                        let background = lighten(theme.palette.background.paper, 0.07);

                        if (index % 2 === 0) {
                            background = darken(background, 0.05);
                        }
                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    background: background,
                                    padding: "10px",
                                }}
                            >
                                <Typography>{entry}</Typography>
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
                    <Tooltip title={"Edit Global Setting"}>
                        <Button
                            onClick={() => {
                                setEditing(true);
                            }}
                        >
                            <EditIcon />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
        );
    } else {
        return (
            <Box>
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }} marginBottom={2}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                            <TextField
                                onChange={(event) => {
                                    setListItemAdd(event.target.value);
                                }}
                                margin="normal"
                                fullWidth
                                id="add_entry"
                                label="Add Entry"
                                name="add_entry"
                                value={listItemAdd}
                                autoFocus
                                size={"small"}
                            />
                            <Tooltip title={"Add Entry to List"}>
                                <Button
                                    onClick={() => {
                                        addEntryToList(listItemAdd);
                                        setListItemAdd("");
                                    }}
                                >
                                    <AddIcon />
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <Tooltip title={"Cancel List Updates"}>
                                <Button
                                    onClick={() => {
                                        resetListEntries();
                                        setEditing(false);
                                    }}
                                >
                                    {"Cancel"}
                                </Button>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <Tooltip title={"Update Global Setting"}>
                                <Button
                                    onClick={() => {
                                        updateSetting(JSON.stringify(listItems));
                                        setEditing(false);
                                        reloadSettings();
                                    }}
                                >
                                    {"Update"}
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
                <Box>
                    <Box>
                        {listItems.map((entry, index) => {
                            let background = lighten(theme.palette.background.paper, 0.07);

                            if (index % 2 === 0) {
                                background = darken(background, 0.05);
                            }

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        background: background,
                                        paddingLeft: "10px",
                                        paddingTop: "10px",
                                    }}
                                >
                                    <Typography>{entry}</Typography>
                                    <Tooltip title={"Delete Entry from List"}>
                                        <Button
                                            onClick={() => {
                                                removeEntryFromList(entry);
                                            }}
                                        >
                                            <DeleteIcon />
                                        </Button>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        );
    }
};
export default ListSetting;
